import { useState, useMemo, useEffect } from 'react'
import type { Apu } from './tipos'
import { recalcularApu } from './tipos'
import { IconoCerrar, IconoMas, IconoBasura } from '@/shared/ui/Iconos/Iconos'
import { CampoPesos } from '../CampoPesos'
import estilos from './BancoApus.module.css'

function formatearMoneda(val: number) {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    maximumFractionDigits: 0,
  }).format(val)
}

type Props = {
  abierto: boolean
  apu: Apu | null
  onCerrar: () => void
  onGuardar: (apuActualizado: Apu) => void
  onAbrirAiu: (apu: Apu) => void
}

export function ModalDetalleApu({ abierto, apu, onCerrar, onGuardar, onAbrirAiu }: Props) {
  const [nombre, setNombre] = useState(apu?.nombre || '')
  const [descripcion, setDescripcion] = useState(apu?.descripcion || '')
  const [categoria, setCategoria] = useState(apu?.categoria || 'General')
  const [unidad, setUnidad] = useState(apu?.unidad || 'UND')
  const [pestañaActiva, setPestañaActiva] = useState<'equipos' | 'materiales' | 'transportes' | 'mano_obra' | 'aiu'>('equipos')

  // Copia mutable para edición
  const [equipos, setEquipos] = useState(() => apu?.equipos || [])
  const [materiales, setMateriales] = useState(() => apu?.materiales || [])
  const [transportes, setTransportes] = useState(() => apu?.transportes || [])
  const [manoObra, setManoObra] = useState(() => apu?.mano_obra || [])
  const [aiu, setAiu] = useState(() => apu?.aiu || { porcentaje_admin: 10, porcentaje_imprevistos: 0, porcentaje_utilidad: 3, porcentaje_iva_utilidad: 19 })

  // Sincronizar estado cuando cambia el APU seleccionado
  useEffect(() => {
    if (apu) {
      setNombre(apu.nombre || '')
      setDescripcion(apu.descripcion || '')
      setCategoria(apu.categoria || 'General')
      setUnidad(apu.unidad || 'UND')
      setEquipos(apu.equipos || [])
      setMateriales(apu.materiales || [])
      setTransportes(apu.transportes || [])
      setManoObra(apu.mano_obra || [])
      setAiu(
        apu.aiu || {
          porcentaje_admin: 10,
          porcentaje_imprevistos: 0,
          porcentaje_utilidad: 3,
          porcentaje_iva_utilidad: 19,
        }
      )
    }
  }, [apu])

  // Recalcular APU dinámicamente con los cambios
  const apuCalculado = useMemo(() => {
    if (!apu) return null
    return recalcularApu({
      id: apu.id,
      codigo: apu.codigo,
      nombre,
      descripcion,
      unidad,
      categoria,
      equipos,
      materiales,
      transportes,
      mano_obra: manoObra,
      aiu,
      proyecto_id: apu.proyecto_id,
      proyecto_nombre: apu.proyecto_nombre,
      activo: apu.activo,
    })
  }, [apu, nombre, descripcion, categoria, unidad, equipos, materiales, transportes, manoObra, aiu])

  if (!abierto || !apu || !apuCalculado) return null

  function handleGuardar() {
    onGuardar(apuCalculado!)
    onCerrar()
  }

  return (
    <div className={estilos.modalVelo} onClick={onCerrar}>
      <div
        className={estilos.modalCajaDetalleApu}
        onClick={(e) => e.stopPropagation()}
      >
        <header className={estilos.modalHeader} style={{ flexShrink: 0 }}>
          <div>
            <h3>Análisis de Precio Unitario (APU): {apu.codigo}</h3>
            <span style={{ fontSize: '0.8rem', opacity: 0.9 }}>
              Total: {formatearMoneda(apuCalculado.precio_unitario_total)} / {apuCalculado.unidad} · Costo Directo: {formatearMoneda(apuCalculado.costo_directo)}
            </span>
          </div>
          <button type="button" className={estilos.btnCerrarModal} onClick={onCerrar} title="Cerrar">
            <IconoCerrar />
          </button>
        </header>

        {/* Barra superior con metadatos de la actividad */}
        <div style={{ padding: '10px 18px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: '8px', flexShrink: 0 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px' }}>
            <div style={{ minWidth: '200px' }}>
              <label style={{ fontSize: '0.68rem', fontWeight: 800, color: '#475569', display: 'block', marginBottom: '2px' }}>NOMBRE DE LA ACTIVIDAD</label>
              <input
                type="text"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                style={{ width: '100%', boxSizing: 'border-box', padding: '5px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontWeight: 700, fontSize: '0.82rem' }}
              />
            </div>
            <div style={{ maxWidth: '100px' }}>
              <label style={{ fontSize: '0.68rem', fontWeight: 800, color: '#475569', display: 'block', marginBottom: '2px' }}>UNIDAD</label>
              <input
                type="text"
                value={unidad}
                onChange={(e) => setUnidad(e.target.value.toUpperCase())}
                style={{ width: '100%', boxSizing: 'border-box', padding: '5px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontWeight: 800, textAlign: 'center', fontSize: '0.82rem' }}
              />
            </div>
            <div style={{ minWidth: '140px' }}>
              <label style={{ fontSize: '0.68rem', fontWeight: 800, color: '#475569', display: 'block', marginBottom: '2px' }}>CATEGORÍA</label>
              <input
                type="text"
                value={categoria}
                onChange={(e) => setCategoria(e.target.value)}
                style={{ width: '100%', boxSizing: 'border-box', padding: '5px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontWeight: 600, fontSize: '0.82rem' }}
              />
            </div>
          </div>
          <div>
            <label style={{ fontSize: '0.68rem', fontWeight: 800, color: '#475569', display: 'block', marginBottom: '2px' }}>ESPECIFICACIÓN / DESCRIPCIÓN TÉCNICA DETALLADA</label>
            <textarea
              rows={1}
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              placeholder="Descripción completa de la actividad o pliego de licitación..."
              style={{ width: '100%', boxSizing: 'border-box', padding: '5px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.78rem', resize: 'vertical' }}
            />
          </div>
        </div>

        {/* Sub-navegación por los 5 componentes en orden secuencial estricto I, II, III, IV, V */}
        <nav style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', background: '#ffffff', padding: '0 16px', gap: '4px', overflowX: 'auto', flexShrink: 0 }}>
          {[
            { id: 'equipos', label: `I. Equipos (${apuCalculado.equipos.length})`, monto: apuCalculado.costo_equipos },
            { id: 'materiales', label: `II. Materiales (${apuCalculado.materiales.length})`, monto: apuCalculado.costo_materiales },
            { id: 'transportes', label: `III. Transportes (${apuCalculado.transportes.length})`, monto: apuCalculado.costo_transportes },
            { id: 'mano_obra', label: `IV. Mano de Obra (${apuCalculado.mano_obra.length})`, monto: apuCalculado.costo_mano_obra },
            { id: 'aiu', label: `V. AIU (${apuCalculado.aiu.porcentaje_admin + apuCalculado.aiu.porcentaje_utilidad + apuCalculado.aiu.porcentaje_imprevistos}%)`, monto: apuCalculado.valor_aiu },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setPestañaActiva(tab.id as any)}
              style={{
                border: 'none',
                background: 'transparent',
                borderBottom: pestañaActiva === tab.id ? '3px solid #2d0177' : '3px solid transparent',
                color: pestañaActiva === tab.id ? '#2d0177' : '#64748b',
                fontWeight: pestañaActiva === tab.id ? 800 : 600,
                fontSize: '0.82rem',
                padding: '10px 14px',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {tab.label} · <span style={{ opacity: 0.85 }}>{formatearMoneda(tab.monto)}</span>
            </button>
          ))}
        </nav>

        {/* Cuerpo del Componente Seleccionado */}
        <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1, minHeight: 0 }}>
          {/* TAB MATERIALES */}
          {pestañaActiva === 'materiales' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <span style={{ fontSize: '0.85rem', fontWeight: 800, color: '#334155' }}>
                  Insumos con Factor de Utilidad / Margen sobre Material
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setMateriales([
                      ...materiales,
                      {
                        id: crypto.randomUUID(),
                        descripcion: 'Nuevo material',
                        unidad: 'UND',
                        costo_base: 10000,
                        factor_margen_pct: 15,
                        costo_unitario: 11500,
                        cantidad: 1,
                        valor_parcial: 11500,
                      },
                    ])
                  }}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#f1f5f9', border: '1px solid #cbd5e1', padding: '4px 10px', borderRadius: '6px', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer' }}
                >
                  <IconoMas style={{ width: '14px', height: '14px' }} />
                  <span>Añadir Material</span>
                </button>
              </div>

              <div style={{ overflowX: 'auto', width: '100%', marginBottom: '10px' }}>
                <table style={{ width: '100%', minWidth: '640px', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0', color: '#475569', textAlign: 'left' }}>
                    <th style={{ padding: '8px' }}>Descripción</th>
                    <th style={{ padding: '8px', width: '65px' }}>Und</th>
                    <th style={{ padding: '8px', width: '120px' }}>Costo Base</th>
                    <th style={{ padding: '8px', width: '85px' }}>Margen %</th>
                    <th style={{ padding: '8px', width: '120px' }}>Costo Venta</th>
                    <th style={{ padding: '8px', width: '75px' }}>Cant</th>
                    <th style={{ padding: '8px', width: '120px', textAlign: 'right' }}>Parcial</th>
                    <th style={{ padding: '8px', width: '35px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {apuCalculado.materiales.map((mat, idx) => (
                    <tr key={mat.id || idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '6px 8px' }}>
                        <input
                          type="text"
                          value={mat.descripcion}
                          onChange={(e) => {
                            const copia = [...materiales]
                            copia[idx] = { ...copia[idx], descripcion: e.target.value }
                            setMateriales(copia)
                          }}
                          style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '4px 8px' }}
                        />
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <input
                          type="text"
                          value={mat.unidad}
                          onChange={(e) => {
                            const copia = [...materiales]
                            copia[idx] = { ...copia[idx], unidad: e.target.value.toUpperCase() }
                            setMateriales(copia)
                          }}
                          style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '4px 6px', textAlign: 'center' }}
                        />
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <CampoPesos
                          valor={mat.costo_base}
                          onCambio={(v) => {
                            const copia = [...materiales]
                            copia[idx] = { ...copia[idx], costo_base: v }
                            setMateriales(copia)
                          }}
                        />
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <input
                          type="number"
                          step="1"
                          value={mat.factor_margen_pct}
                          onChange={(e) => {
                            const copia = [...materiales]
                            copia[idx] = { ...copia[idx], factor_margen_pct: Number(e.target.value) || 0 }
                            setMateriales(copia)
                          }}
                          style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '4px 6px', textAlign: 'center', fontWeight: 700, color: '#16a34a' }}
                        />
                      </td>
                      <td style={{ padding: '6px 8px', fontWeight: 700, color: '#1e293b' }}>
                        {formatearMoneda(mat.costo_unitario)}
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <input
                          type="number"
                          step="any"
                          value={mat.cantidad}
                          onChange={(e) => {
                            const copia = [...materiales]
                            copia[idx] = { ...copia[idx], cantidad: Number(e.target.value) || 0 }
                            setMateriales(copia)
                          }}
                          style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '4px 6px', textAlign: 'center', fontWeight: 800 }}
                        />
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 800, color: '#2d0177' }}>
                        {formatearMoneda(mat.valor_parcial)}
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                        <button
                          type="button"
                          onClick={() => setMateriales(materiales.filter((_, i) => i !== idx))}
                          style={{ border: 'none', background: 'transparent', color: '#ef4444', cursor: 'pointer' }}
                          title="Quitar material"
                        >
                          <IconoBasura style={{ width: '14px', height: '14px' }} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          )}

          {/* TAB MANO DE OBRA */}
          {pestañaActiva === 'mano_obra' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <span style={{ fontSize: '0.85rem', fontWeight: 800, color: '#334155' }}>
                  Cuadrillas, Salarios Base y Rendimiento en Horas Hombre (HH)
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setManoObra([
                      ...manoObra,
                      {
                        id: crypto.randomUUID(),
                        cargo: 'Técnico Electricista',
                        salario_base: 184558.4,
                        costo_dia: 184558.4,
                        rendimiento: 0.1,
                        horas_hombre: 0.8,
                        costo_total: 18455.84,
                      },
                    ])
                  }}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#f1f5f9', border: '1px solid #cbd5e1', padding: '4px 10px', borderRadius: '6px', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer' }}
                >
                  <IconoMas style={{ width: '14px', height: '14px' }} />
                  <span>Añadir Cargo</span>
                </button>
              </div>

              <div style={{ overflowX: 'auto', width: '100%', marginBottom: '10px' }}>
                <table style={{ width: '100%', minWidth: '640px', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0', color: '#475569', textAlign: 'left' }}>
                    <th style={{ padding: '8px' }}>Cargo / Categoría</th>
                    <th style={{ padding: '8px', width: '140px' }}>Costo Día ($ COP)</th>
                    <th style={{ padding: '8px', width: '100px' }}>Rendimiento (Día)</th>
                    <th style={{ padding: '8px', width: '90px' }}>HH / Unidad</th>
                    <th style={{ padding: '8px', width: '130px', textAlign: 'right' }}>Costo Total</th>
                    <th style={{ padding: '8px', width: '35px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {apuCalculado.mano_obra.map((mo, idx) => (
                    <tr key={mo.id || idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '6px 8px' }}>
                        <input
                          type="text"
                          value={mo.cargo}
                          onChange={(e) => {
                            const copia = [...manoObra]
                            copia[idx] = { ...copia[idx], cargo: e.target.value }
                            setManoObra(copia)
                          }}
                          style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '4px 8px' }}
                        />
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <CampoPesos
                          valor={mo.costo_dia}
                          onCambio={(v) => {
                            const copia = [...manoObra]
                            copia[idx] = { ...copia[idx], costo_dia: v }
                            setManoObra(copia)
                          }}
                        />
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <input
                          type="number"
                          step="any"
                          value={mo.rendimiento}
                          onChange={(e) => {
                            const copia = [...manoObra]
                            copia[idx] = { ...copia[idx], rendimiento: Number(e.target.value) || 0 }
                            setManoObra(copia)
                          }}
                          style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '4px 6px', textAlign: 'center', fontWeight: 700 }}
                        />
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'center', fontWeight: 800, color: '#6d28d9' }}>
                        {mo.horas_hombre} h
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 800, color: '#2d0177' }}>
                        {formatearMoneda(mo.costo_total)}
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                        <button
                          type="button"
                          onClick={() => setManoObra(manoObra.filter((_, i) => i !== idx))}
                          style={{ border: 'none', background: 'transparent', color: '#ef4444', cursor: 'pointer' }}
                          title="Quitar cargo"
                        >
                          <IconoBasura style={{ width: '14px', height: '14px' }} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>

              <div style={{ marginTop: '14px', padding: '10px 14px', background: '#f5f3ff', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#5b21b6' }}>
                  Total Horas Hombre requeridas por unidad:
                </span>
                <span style={{ fontSize: '1rem', fontWeight: 900, color: '#5b21b6' }}>
                  {apuCalculado.horas_hombre_unidad} HH
                </span>
              </div>
            </div>
          )}

          {/* TAB EQUIPOS */}
          {pestañaActiva === 'equipos' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <span style={{ fontSize: '0.85rem', fontWeight: 800, color: '#334155' }}>
                  Herramientas Menores, Equipos de Alturas y Medición
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setEquipos([
                      ...equipos,
                      {
                        id: crypto.randomUUID(),
                        descripcion: 'Equipo o herramienta',
                        unidad: 'EQU',
                        tarifa: 16693.75,
                        rendimiento: 0.1,
                        valor_unitario: 1669.38,
                      },
                    ])
                  }}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#f1f5f9', border: '1px solid #cbd5e1', padding: '4px 10px', borderRadius: '6px', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer' }}
                >
                  <IconoMas style={{ width: '14px', height: '14px' }} />
                  <span>Añadir Equipo</span>
                </button>
              </div>

              <div style={{ overflowX: 'auto', width: '100%', marginBottom: '10px' }}>
                <table style={{ width: '100%', minWidth: '640px', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0', color: '#475569', textAlign: 'left' }}>
                    <th style={{ padding: '8px' }}>Descripción</th>
                    <th style={{ padding: '8px', width: '80px' }}>Und</th>
                    <th style={{ padding: '8px', width: '140px' }}>Tarifa ($ COP)</th>
                    <th style={{ padding: '8px', width: '100px' }}>Rendimiento</th>
                    <th style={{ padding: '8px', width: '130px', textAlign: 'right' }}>Valor Parcial</th>
                    <th style={{ padding: '8px', width: '35px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {apuCalculado.equipos.map((eq, idx) => (
                    <tr key={eq.id || idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '6px 8px' }}>
                        <input
                          type="text"
                          value={eq.descripcion}
                          onChange={(e) => {
                            const copia = [...equipos]
                            copia[idx] = { ...copia[idx], descripcion: e.target.value }
                            setEquipos(copia)
                          }}
                          style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '4px 8px' }}
                        />
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <input
                          type="text"
                          value={eq.unidad}
                          onChange={(e) => {
                            const copia = [...equipos]
                            copia[idx] = { ...copia[idx], unidad: e.target.value.toUpperCase() }
                            setEquipos(copia)
                          }}
                          style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '4px 6px', textAlign: 'center' }}
                        />
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <CampoPesos
                          valor={eq.tarifa}
                          onCambio={(v) => {
                            const copia = [...equipos]
                            copia[idx] = { ...copia[idx], tarifa: v }
                            setEquipos(copia)
                          }}
                        />
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <input
                          type="number"
                          step="any"
                          value={eq.rendimiento}
                          onChange={(e) => {
                            const copia = [...equipos]
                            copia[idx] = { ...copia[idx], rendimiento: Number(e.target.value) || 0 }
                            setEquipos(copia)
                          }}
                          style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '4px 6px', textAlign: 'center', fontWeight: 700 }}
                        />
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 800, color: '#2d0177' }}>
                        {formatearMoneda(eq.valor_unitario)}
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                        <button
                          type="button"
                          onClick={() => setEquipos(equipos.filter((_, i) => i !== idx))}
                          style={{ border: 'none', background: 'transparent', color: '#ef4444', cursor: 'pointer' }}
                          title="Quitar equipo"
                        >
                          <IconoBasura style={{ width: '14px', height: '14px' }} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          )}

          {/* TAB TRANSPORTES */}
          {pestañaActiva === 'transportes' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <span style={{ fontSize: '0.85rem', fontWeight: 800, color: '#334155' }}>
                  Vehículos, Fletes y Acarreos
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setTransportes([
                      ...transportes,
                      {
                        id: crypto.randomUUID(),
                        descripcion: 'Camioneta 4x4 cuadrilla',
                        volumen_o_peso: 1,
                        rendimiento: 0.01,
                        costo_tarifa: 130500,
                        valor_parcial: 1305,
                      },
                    ])
                  }}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#f1f5f9', border: '1px solid #cbd5e1', padding: '4px 10px', borderRadius: '6px', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer' }}
                >
                  <IconoMas style={{ width: '14px', height: '14px' }} />
                  <span>Añadir Transporte</span>
                </button>
              </div>

              <div style={{ overflowX: 'auto', width: '100%', marginBottom: '10px' }}>
                <table style={{ width: '100%', minWidth: '640px', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0', color: '#475569', textAlign: 'left' }}>
                    <th style={{ padding: '8px' }}>Descripción</th>
                    <th style={{ padding: '8px', width: '80px' }}>Vol/Peso</th>
                    <th style={{ padding: '8px', width: '140px' }}>Costo Tarifa ($ COP)</th>
                    <th style={{ padding: '8px', width: '100px' }}>Rendimiento</th>
                    <th style={{ padding: '8px', width: '130px', textAlign: 'right' }}>Valor Parcial</th>
                    <th style={{ padding: '8px', width: '35px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {apuCalculado.transportes.map((tr, idx) => (
                    <tr key={tr.id || idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '6px 8px' }}>
                        <input
                          type="text"
                          value={tr.descripcion}
                          onChange={(e) => {
                            const copia = [...transportes]
                            copia[idx] = { ...copia[idx], descripcion: e.target.value }
                            setTransportes(copia)
                          }}
                          style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '4px 8px' }}
                        />
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <input
                          type="number"
                          step="any"
                          value={tr.volumen_o_peso}
                          onChange={(e) => {
                            const copia = [...transportes]
                            copia[idx] = { ...copia[idx], volumen_o_peso: Number(e.target.value) || 0 }
                            setTransportes(copia)
                          }}
                          style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '4px 6px', textAlign: 'center' }}
                        />
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <CampoPesos
                          valor={tr.costo_tarifa}
                          onCambio={(v) => {
                            const copia = [...transportes]
                            copia[idx] = { ...copia[idx], costo_tarifa: v }
                            setTransportes(copia)
                          }}
                        />
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <input
                          type="number"
                          step="any"
                          value={tr.rendimiento}
                          onChange={(e) => {
                            const copia = [...transportes]
                            copia[idx] = { ...copia[idx], rendimiento: Number(e.target.value) || 0 }
                            setTransportes(copia)
                          }}
                          style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '4px 6px', textAlign: 'center', fontWeight: 700 }}
                        />
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 800, color: '#2d0177' }}>
                        {formatearMoneda(tr.valor_parcial)}
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                        <button
                          type="button"
                          onClick={() => setTransportes(transportes.filter((_, i) => i !== idx))}
                          style={{ border: 'none', background: 'transparent', color: '#ef4444', cursor: 'pointer' }}
                          title="Quitar transporte"
                        >
                          <IconoBasura style={{ width: '14px', height: '14px' }} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          )}

          {/* TAB AIU */}
          {pestañaActiva === 'aiu' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                <span style={{ fontSize: '0.85rem', fontWeight: 800, color: '#334155' }}>
                  Costos Indirectos (AIU) sobre Costo Directo
                </span>
                <button
                  type="button"
                  onClick={() => onAbrirAiu(apuCalculado)}
                  style={{ background: '#fdf2f8', border: '1px solid #f472b6', color: '#be185d', padding: '4px 12px', borderRadius: '6px', fontSize: '0.78rem', fontWeight: 800, cursor: 'pointer' }}
                >
                  Abrir Ajuste Rápido de AIU
                </button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '10px', marginBottom: '16px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '0.74rem', fontWeight: 700 }}>Administración %</label>
                  <input
                    type="number"
                    step="0.5"
                    value={aiu.porcentaje_admin}
                    onChange={(e) => setAiu({ ...aiu, porcentaje_admin: Number(e.target.value) || 0 })}
                    style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontWeight: 700 }}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '0.74rem', fontWeight: 700 }}>Imprevistos %</label>
                  <input
                    type="number"
                    step="0.5"
                    value={aiu.porcentaje_imprevistos}
                    onChange={(e) => setAiu({ ...aiu, porcentaje_imprevistos: Number(e.target.value) || 0 })}
                    style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontWeight: 700 }}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '0.74rem', fontWeight: 700 }}>Utilidad %</label>
                  <input
                    type="number"
                    step="0.5"
                    value={aiu.porcentaje_utilidad}
                    onChange={(e) => setAiu({ ...aiu, porcentaje_utilidad: Number(e.target.value) || 0 })}
                    style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontWeight: 700 }}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '0.74rem', fontWeight: 700 }}>IVA s/ Utilidad %</label>
                  <input
                    type="number"
                    step="0.5"
                    value={aiu.porcentaje_iva_utilidad}
                    onChange={(e) => setAiu({ ...aiu, porcentaje_iva_utilidad: Number(e.target.value) || 0 })}
                    style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontWeight: 700 }}
                  />
                </div>
              </div>

              <div style={{ background: '#f8fafc', padding: '12px 16px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '8px' }}>
                <table style={{ width: '100%', fontSize: '0.84rem' }}>
                  <tbody>
                    <tr><td>Costo Directo Base:</td><td style={{ textAlign: 'right', fontWeight: 700 }}>{formatearMoneda(apuCalculado.costo_directo)}</td></tr>
                    <tr><td>+ Administración ({aiu.porcentaje_admin}%):</td><td style={{ textAlign: 'right' }}>+{formatearMoneda(apuCalculado.valor_admin)}</td></tr>
                    <tr><td>+ Imprevistos ({aiu.porcentaje_imprevistos}%):</td><td style={{ textAlign: 'right' }}>+{formatearMoneda(apuCalculado.valor_imprevistos)}</td></tr>
                    <tr><td>+ Utilidad ({aiu.porcentaje_utilidad}%):</td><td style={{ textAlign: 'right' }}>+{formatearMoneda(apuCalculado.valor_utilidad)}</td></tr>
                    <tr><td>+ IVA s/ Utilidad ({aiu.porcentaje_iva_utilidad}%):</td><td style={{ textAlign: 'right' }}>+{formatearMoneda(apuCalculado.valor_iva_utilidad)}</td></tr>
                    <tr style={{ borderTop: '2px solid #2d0177', fontSize: '0.96rem', color: '#2d0177', fontWeight: 900 }}>
                      <td style={{ paddingTop: '8px' }}>PRECIO UNITARIO TOTAL:</td>
                      <td style={{ paddingTop: '8px', textAlign: 'right' }}>{formatearMoneda(apuCalculado.precio_unitario_total)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <footer className={estilos.modalFooter} style={{ flexShrink: 0 }}>
          <button type="button" className={estilos.btnCancelar} onClick={onCerrar}>
            Cancelar
          </button>
          <button type="button" className={estilos.btnGuardar} onClick={handleGuardar}>
            Guardar Cambios de la Actividad
          </button>
        </footer>
      </div>
    </div>
  )
}

